/**
 * Subscription pricing — the SINGLE SOURCE OF TRUTH for plan figures + the web
 * pricing rules. Imported by the API (Phase 7), the PWA subscription screen, and
 * the marketing site so the three can never drift (CLAUDE.md → packages/shared).
 *
 * These are the CONFIRMED launch figures (decided 2026-06-08): Pro £2.99/mo,
 * Business £14.99/mo. The Stripe sandbox catalogue mirrors the derived amounts
 * (see DECISIONS.md → "Phase 7 prep: Stripe sandbox products"). Stripe holds the
 * authoritative prices that are actually charged; these values are for *display*
 * and must be kept in sync with the Stripe Price objects.
 *
 * Web pricing rules (this is the WEB property — only ever show web prices here;
 * NEVER surface Apple-vs-web price differences, for App Store anti-steering
 * hygiene):
 *   - yearly   = 20% below 12× monthly   (yearlyTotal = monthly × 12 × 0.8)
 *   - lifetime = 2.5× the yearly price    (lifetime   = yearlyTotal × 2.5)
 *
 * Lifetime is a one-time payment, not a recurring subscription. There is no free
 * trial — the Free tier is the trial. Currency is GBP only at launch.
 */

/** The two paid plans. (Free is the default, non-purchasable tier.) */
export type PaidPlan = 'pro' | 'business';

/** Purchase cadences offered on the web. */
export type BillingCadence = 'monthly' | 'yearly' | 'lifetime';

/** Confirmed base monthly price per paid plan, in GBP. */
export const MONTHLY_GBP: Record<PaidPlan, number> = {
  pro: 2.99,
  business: 14.99,
};

/** Web yearly is 20% below 12× monthly. */
const YEARLY_DISCOUNT = 0.2;
/** Web lifetime is 2.5× the yearly price. */
const LIFETIME_MULTIPLIER = 2.5;

/** Total billed once a year for the yearly plan. */
export function yearlyTotal(plan: PaidPlan): number {
  return MONTHLY_GBP[plan] * 12 * (1 - YEARLY_DISCOUNT);
}

/** The per-month figure to show under a yearly plan. */
export function yearlyPerMonth(plan: PaidPlan): number {
  return yearlyTotal(plan) / 12;
}

/** One-time lifetime price for a plan. */
export function lifetimePrice(plan: PaidPlan): number {
  return yearlyTotal(plan) * LIFETIME_MULTIPLIER;
}

/** Display the GBP price the customer sees (2 decimal places). */
export function formatGBP(amount: number): string {
  return `£${amount.toFixed(2)}`;
}
